import { useCallback, useEffect, useState } from 'react';
import {
  Avatar, Badge, Card, Center, Group, SimpleGrid, Stack, Text, ThemeIcon, ActionIcon, Tooltip,
} from '@mantine/core';
import { IconUsers, IconRefresh } from '@tabler/icons-react';
import { MemberDetailModal } from '@/components';
import { useRegistrationService, useMemberService } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';

interface MembersTabProps {
  registrationId: number;
  active: boolean;
}

// Shape from searchMembers response data[]
interface Member {
  memberID: number;
  userId: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  dateOfBirth: string | null;
  gender: string | null;
  maritalStatusID: number;
  email: string;
  cellNumber: string | null;
  altContact: string | null;
  telephone: string | null;
  isMember: boolean;
  isActive: boolean;
  avatar: string | null;
  typeID: number;
  memberCode: string | null;
  modifiedDateTime: number;
  createdDateTime: number | null;
  // joined locally after enrichment
  isMain?: boolean;
  /** Registered the enrolment, as opposed to someone who is only enrolled (SDK 0.0.58). */
  isApplicant?: boolean;
  hasConsent?: boolean;
}

const fullName = (m: Member) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || `Member ${m.memberID}`;

const getInitials = (m: Member) =>
  [m.firstName?.[0], m.lastName?.[0]].filter(Boolean).join('').toUpperCase() || 'M';

// ── Member grid card (view only) ──────────────────────────────────────────────
function MemberCard({ member, onOpenDetails }: { member: Member; onOpenDetails: () => void }) {
  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      h="100%"
      onClick={onOpenDetails}
      style={{ cursor: 'pointer' }}
    >
      <Group justify="center" mb="sm">
        <Avatar src={member.avatar ?? undefined} alt={fullName(member)} size="xl" radius="md">
          {getInitials(member)}
        </Avatar>
      </Group>

      <Text fw={700} ta="center" size="md" mb={4} lineClamp={1}>
        {fullName(member)}
      </Text>

      {/* ⚠️ Independent flags: a self-registration is both applicant and enrolled; someone who
          registered another person is the applicant but not a participant. */}
      <Group justify="center" gap={6} mb={4}>
        {member.isApplicant && (
          <Badge size="sm" variant="light" color="grape">Applicant</Badge>
        )}
        {member.isMain && (
          <Badge size="sm" variant="light" color="blue">Main Participant</Badge>
        )}
        <Badge size="sm" variant="light" color={member.hasConsent ? 'green' : 'red'}>
          {member.hasConsent ? 'Consent Given' : 'No Consent'}
        </Badge>
      </Group>

      <Stack align="center" gap={2} mt="xs">
        {member.email
          ? <Text size="sm" c="dimmed">Email: {member.email}</Text>
          : <Text size="sm" c="dimmed">No email on record</Text>
        }
        {(member.cellNumber || member.telephone || member.altContact)
          ? <Text size="sm" c="dimmed">Phone: {member.cellNumber ?? member.telephone ?? member.altContact}</Text>
          : <Text size="sm" c="dimmed">No phone on record</Text>
        }
      </Stack>
    </Card>
  );
}

function MemberCardSkeleton() {
  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm">
        <Avatar radius="xl" size={52} color="gray">M</Avatar>
        <Stack gap={4}>
          <Text size="sm" c="dimmed">Loading…</Text>
          <Text size="xs" c="dimmed">—</Text>
          <Text size="xs" c="dimmed">—</Text>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MembersTab({ registrationId, active }: MembersTabProps) {
  const registrationService = useRegistrationService();
  const memberService = useMemberService();

  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  /** Card click opens the read-only detail modal. */
  const [detailMember, setDetailMember] = useState<Member | null>(null);

  /**
   * Membership comes from `getMembers()`; the display details come from `getView().participants`.
   *
   * ⚠️ **These are two different sources and they can disagree.** `getMembers()` returns the
   * `MemberRegistration` junction rows, the actual "who is on this registration" list.
   * `getView().participants` is built from the staged participant *details*, so a member linked
   * without a matching detail row does **not** appear there. Membership must win.
   *
   * The enrichment is still one call in the normal case: only members with no matching participant row
   * fall back to a per-member lookup, which is usually none.
   */
  const fetchMembers = useCallback(async () => {
    if (!registrationId) return;
    setLoadingMembers(true);
    try {
      const [rawLinks, view] = await Promise.all([
        registrationService.getMembers(registrationId),
        registrationService.getView(registrationId).catch(() => null),
      ]);

      const links = ((rawLinks as unknown as { data?: unknown })?.data ?? rawLinks) as
        { memberID: number; isMain: boolean; isApplicant: boolean; hasConsent: boolean }[];
      const linkRows = Array.isArray(links) ? links : [];

      const byMemberID = new Map(
        (view?.participants ?? [])
          .filter((p) => p.memberID != null)
          .map((p) => [p.memberID as number, p]),
      );

      const enriched = await Promise.all(linkRows.map(async (link) => {
        const p = byMemberID.get(link.memberID);
        if (p) {
          return {
            memberID: link.memberID,
            userId: '',
            firstName: (p.name ?? '').trim() || 'Member',
            middleName: null,
            lastName: (p.surname ?? '').trim(),
            dateOfBirth: p.dateOfBirth ?? null,
            gender: p.gender ?? null,
            maritalStatusID: 0,
            email: p.email ?? '',
            cellNumber: p.phone ?? null,
            altContact: null,
            telephone: null,
            isMember: p.isMember,
            isActive: true,
            avatar: p.avatar ?? null,
            typeID: 0,
            memberCode: null,
            modifiedDateTime: 0,
            createdDateTime: null,
            isMain: link.isMain,
            isApplicant: link.isApplicant,
            hasConsent: link.hasConsent,
          };
        }

        // ⚠️ Linked but not in the view's participants — the case that used to vanish silently.
        try {
          const res: any = await memberService.searchMembers(String(link.memberID));
          const list: any[] = res?.data ?? res ?? [];
          const profile = list.find((m) => m.memberID === link.memberID) ?? list[0];
          if (profile) {
            return {
              ...profile,
              isMain: link.isMain,
              isApplicant: link.isApplicant,
              hasConsent: link.hasConsent,
            };
          }
        } catch { /* fall through to the bare row */ }

        return {
          memberID: link.memberID,
          userId: '',
          firstName: 'Member',
          middleName: null,
          lastName: String(link.memberID),
          dateOfBirth: null,
          gender: null,
          maritalStatusID: 0,
          email: '',
          cellNumber: null,
          altContact: null,
          telephone: null,
          isMember: true,
          isActive: true,
          avatar: null,
          typeID: 0,
          memberCode: null,
          modifiedDateTime: 0,
          createdDateTime: null,
          isMain: link.isMain,
          isApplicant: link.isApplicant,
          hasConsent: link.hasConsent,
        };
      }));

      setMembers(enriched as any);
    } catch (e) {
      console.error('Error fetching registration members:', e);
      toast.error('Error', 'Could not load participants.');
    } finally {
      setLoadingMembers(false);
    }
  }, [registrationId, registrationService, memberService]);

  useEffect(() => { if (active) fetchMembers(); }, [active, fetchMembers]);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm" c="dimmed">
            Members on this registration ({members.length})
          </Text>

          <Tooltip label="Refresh members" withArrow>
            <ActionIcon variant="subtle" onClick={fetchMembers} loading={loadingMembers} size="lg">
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {loadingMembers ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <MemberCardSkeleton />
            <MemberCardSkeleton />
            <MemberCardSkeleton />
          </SimpleGrid>
        ) : members.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={56} radius="xl" variant="light" color="teal">
                <IconUsers size={30} />
              </ThemeIcon>
              <Text fw={600}>No members yet</Text>
              <Text size="sm" c="dimmed" ta="center" maw={360}>
                Nobody has been added to this registration.
              </Text>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {members.map((c) => (
              <MemberCard key={c.memberID} member={c} onOpenDetails={() => setDetailMember(c)} />
            ))}
          </SimpleGrid>
        )}
      </Stack>

      {/* The card only has room for a name and one contact line; the rest lives here. */}
      <MemberDetailModal
        member={detailMember}
        opened={detailMember !== null}
        onClose={() => setDetailMember(null)}
        contextLabel="registration"
      />
    </Stack>
  );
}
